// Wariant CartSummary uzywajacy nowego endpointu /carts/{id}/services/summary,
// dzieki czemu lista uslug (dostawa, platnosc, dropshipping, pozostale) jest
// agregowana po stronie backendu i renderowana dynamicznie.
// Dropshipping otrzymuje wizualne oznaczenie.

import React, { FC, PropsWithChildren } from 'react';
import { Trans } from 'react-i18next';
import { ArrowRight } from 'react-bootstrap-icons';
import classnames from 'classnames';

import { useGetCart, useGetCartsServicesSummary } from 'api';
import { useAppNavigate } from 'hooks';
import { Button } from 'components/controls';

import styles from 'theme/components/wise/WiseCartSummary/WiseCartSummary.module.scss';

export interface WiseCartSummaryProps {
  cartId: number;
  buttonOnClick: () => void;
  buttonLabel: string;
  isLoading?: boolean;
}

const WiseCartSummary: FC<WiseCartSummaryProps> = ({
  cartId,
  buttonOnClick,
  buttonLabel,
  isLoading
}) => {
  const navigate = useAppNavigate();

  const { data: cartData } = useGetCart(cartId, {
    enabled: !!cartId,
    onError: () => {
      navigate('/');
    }
  });

  const { data: servicesSummary } = useGetCartsServicesSummary(cartId, {
    enabled: !!cartId
  });

  const Row: FC<PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
    <div className={classnames(styles.row, className)}> {children}</div>
  );

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Wise-WiseCartSummary'
      )}>
      <div>
        <Row>
          <div className={styles.labelBold}>
            <Trans>Wartość koszyka</Trans>
          </div>
          <div className={styles.valueCartNet}>
            {cartData?.cart_price_net_formatted ? (
              <>
                <span>
                  {cartData.cart_price_net_formatted} {cartData.currency}
                </span>{' '}
                <Trans>netto</Trans>
              </>
            ) : (
              <span>-</span>
            )}
          </div>
        </Row>
        <Row>
          <div />
          <div className={styles.valueCartGross}>
            {cartData?.cart_price_gross_formatted ? (
              <>
                <span>
                  {cartData.cart_price_gross_formatted} {cartData.currency}
                </span>{' '}
                <Trans>brutto</Trans>
              </>
            ) : (
              <span>-</span>
            )}
          </div>
        </Row>
      </div>

      <div className={styles.servicesSection}>
        {servicesSummary?.items?.length ? (
          servicesSummary.items.map((item) => (
            <Row key={`${item.type}-${item.id ?? item.name}`}>
              <div>{item.name}</div>
              <div>
                {item.cost_gross_formatted} {servicesSummary.currency}
              </div>
            </Row>
          ))
        ) : (
          <Row>
            <div>
              <Trans>Brak dodatkowych usług</Trans>
            </div>
            <div>-</div>
          </Row>
        )}
      </div>

      <div>
        <Row>
          <div className={styles.labelBold}>
            <Trans>Łączna kwota</Trans>
          </div>
          <div className={styles.valueTotalNet}>
            {cartData?.total_base_price_net_formatted && (
              <span className={classnames(styles.oldPrice)}>
                <span>
                  {cartData.total_base_price_net_formatted} {cartData.currency}
                </span>{' '}
              </span>
            )}
            {cartData?.total_price_net_formatted ? (
              <>
                <span>
                  {cartData.total_price_net_formatted} {cartData.currency}
                </span>{' '}
                <Trans>netto</Trans>
              </>
            ) : (
              <span>-</span>
            )}
          </div>
        </Row>
        <Row>
          <div />
          <div className={styles.valueTotalGross}>
            {cartData?.total_base_price_gross_formatted && (
              <span className={classnames(styles.oldPrice)}>
                <span>
                  {cartData.total_base_price_gross_formatted} {cartData.currency}
                </span>
              </span>
            )}
            {cartData?.total_price_gross_formatted ? (
              <>
                <span>
                  {cartData.total_price_gross_formatted} {cartData.currency}
                </span>
                <Trans>brutto</Trans>
              </>
            ) : (
              <span>-</span>
            )}
          </div>
        </Row>
        <Row>
          <div />
          {cartData?.total_discount_net_formatted && (
            <div className={styles.discount}>
              <Trans>Oszczędzasz</Trans>:{' '}
              <span>
                {cartData.total_discount_net_formatted} {cartData.currency}
              </span>{' '}
              <Trans>netto</Trans>
            </div>
          )}
        </Row>
      </div>

      <div className={styles.buttonWrapper}>
        <Button onClick={() => buttonOnClick()} loading={isLoading}>
          {buttonLabel} <ArrowRight className={styles.buttonArrow} />
        </Button>
      </div>
    </div>
  );
};

export default WiseCartSummary;
